import React, { useState } from 'react';
import Markdown from 'react-markdown';

import styles from './Layouters.module.scss';
import docStyles from '../chart-types-documentation/ChartTypesDocumentation.module.scss';
import docViewerStyles from '../widget-doc-viewer/WidgetDocViewer.module.scss';

import { TpDashboardSectionDefinition, TpDashboardContext } from '../dashboard/interfaces';
import { TpDataContext } from '../../../data/interfaces';
import { renderDashboardElement } from '../dashboard/render-elem';
import { dashboardWidgetPadding } from '../dashboard/layout-hints';
import { useAppDispatch } from '../../../app/hooks';
import { ElementEditTools } from '../dashboard/edit-mode/ElementEditTools';
import { toggleSectionCollapse } from '../data/dashboardsSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SectionIndex } from '../../../components/sections-index/SectionsIndex';
import { useEventNotification } from '../hooks/events';
import { BlockingOpenedItem } from '../../../components/blocking-opened-item/BlockingOpenedItem';

interface TpDashboardSectionProps {
  dataContext: TpDataContext;
  dashboardContext: TpDashboardContext;
  sectionDef: TpDashboardSectionDefinition;
}

function DashboardSection({ dataContext, sectionDef, dashboardContext }: TpDashboardSectionProps) {
  const dispatch = useAppDispatch();
  const [docsActive, setDocsActive] = useState(false);

  useEventNotification(
    'ExpandCollapsedSection',
    (tpe: string, messageBody: { sectionedDocId: string; sectionId: string }) => {
      if (
        sectionDef.id == messageBody.sectionId &&
        dashboardContext.sectionIndexContext?.sectionedDocId == messageBody.sectionedDocId &&
        sectionDef.collapsed
      ) {
        dispatch(
          toggleSectionCollapse({
            dashboardDefId: dashboardContext.dashboardDefId,
            id: sectionDef.id,
          }),
        );
      }
    },
    [sectionDef],
  );

  return (
    <SectionIndex
      sectionIndexContext={dashboardContext.sectionIndexContext}
      sectionId={sectionDef.id}
      sectionName={sectionDef.title}
    >
      <div style={{ width: '100%', position: 'relative' }}>
        <div
          className={styles.sectionTitle}
          onClick={() =>
            dispatch(
              toggleSectionCollapse({
                dashboardDefId: dashboardContext.dashboardDefId,
                id: sectionDef.id,
              }),
            )
          }
        >
          <div className={styles.sectionTitleText}>
            {sectionDef.title}
            {sectionDef.description && (
              <FontAwesomeIcon
                icon={['fad', 'info-circle']}
                className={styles.sectionTitleInformationButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setDocsActive(true);
                }}
              />
            )}
          </div>
          <div className={styles.sectionTitleButton}>
            {sectionDef.collapsed ? <FontAwesomeIcon icon="chevron-down" /> : <FontAwesomeIcon icon="chevron-up" />}
          </div>
          {sectionDef.collapsed && (
            <div className={styles.sectionCollapsedIndicator}>
              <FontAwesomeIcon icon="plus" />
            </div>
          )}
        </div>
        {!sectionDef.collapsed && (
          <div className={styles.sectionBody}>
            {sectionDef.elements.map((el, idx) => (
              <React.Fragment key={`idx_${idx}`}>
                <div>{renderDashboardElement(sectionDef, el, dataContext, dashboardContext)}</div>
                {idx < sectionDef.elements.length - 1 && (
                  <div style={{ backgroundColor: 'transparent', height: `${dashboardWidgetPadding}px` }} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        {dashboardContext.editMode && (
          <ElementEditTools
            dataContext={dataContext}
            dashboardContext={dashboardContext}
            parentElemDef={null}
            elemDef={sectionDef}
            dispatch={dispatch}
          />
        )}
      </div>
      {docsActive && (
        <BlockingOpenedItem
          fraction={80}
          close={() => {
            setDocsActive(false);
          }}
        >
          <div className={docStyles.detailedContainer}>
            <h1>
              <FontAwesomeIcon icon={['fad', 'info-circle']} className={docViewerStyles.title} />
            </h1>
            <Markdown>{sectionDef.description!}</Markdown>
          </div>
        </BlockingOpenedItem>
      )}
    </SectionIndex>
  );
}

export default DashboardSection;
